'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from 'posthog-js/react';
import { I18N_CONF } from '@funkey/locales/config';
import { useI18n } from '~/features/i18n';
import { ButtonLink } from '~/components/primitives';
import Icon from '~/components/primitives/Icon';
import Shape from '~/components/primitives/Shape';
import { cn } from '~/utils';
import { Units } from '~/modules/units';
import { useSitePhoneNumber } from '~/features/settings/SiteContact';

/** Below this the number argues against the band rather than for it: "Geen zin
 * om 11 teambuildings te doorzoeken?" reads as though eleven were a chore. The
 * line drops the count and says "alle" instead. */
const PROMOTION_COUNT_WORTH_NAMING = 50;

/** A band across the results after the first row, for larger groups: a line to a human,
 * by form and by phone. Both clicks count as a click-through in the
 * experiment, so each is tracked. */
export const PlpPromotionCard: React.FC<{
  participants: number;
  /** How many teambuildings the visitor would otherwise have to go through. */
  activityCount: number;
  className?: string;
}> = props => {
  const $ = _c(38);
  const i18n = useI18n();
  const posthog = usePostHog();
  const phoneNumber = useSitePhoneNumber();
  let t0;
  if ($[0] !== props.className) {
    t0 = cn("@3xl:flex-row @3xl:items-center @3xl:gap-8 @3xl:px-10 relative isolate flex flex-col justify-between gap-6 overflow-hidden rounded-3xl bg-black/5 px-6 py-10", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Shape type="pacMan" className="absolute -bottom-16 -left-14 -z-10 hidden scale-75 rotate-30 @3xl:block" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== i18n || $[4] !== props.activityCount) {
    t2 = props.activityCount < PROMOTION_COUNT_WORTH_NAMING ? i18n._("activity.plp.promotion.titleWithoutCount") : i18n._("activity.plp.promotion.title", {
      count: Units.formatNumberWithThousandSeparator(props.activityCount, i18n.locale)
    });
    $[3] = i18n;
    $[4] = props.activityCount;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <h2 className="text-lg leading-snug font-bold text-blue">{t2}</h2>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== i18n) {
    t4 = i18n._("activity.plp.promotion.description");
    $[8] = i18n;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <p className="max-w-2xl text-sm leading-relaxed text-blue-500 md:text-base">{t4}</p>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <div className="flex min-w-0 flex-col gap-2">{t3}{t5}</div>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== i18n || $[16] !== phoneNumber || $[17] !== posthog || $[18] !== props.participants) {
    t7 = phoneNumber && <a href={`tel:${phoneNumber.replaceAll(" ", "")}`} className="group/phone hit-area-2 flex items-center gap-3 text-base font-bold text-blue" onClick={() => posthog.capture("plp.promotion.click", {
      channel: "phone",
      participants: props.participants
    })}><Icon name="phone" className="size-5 shrink-0 text-blue" strokeWidth={1.5} /><span className="flex flex-col leading-tight"><span className="underline-offset-4 group-hover/phone:underline">{phoneNumber}</span><span className="text-xs font-normal text-gray-500">{i18n._("activity.plp.promotion.hours")}</span></span></a>;
    $[15] = i18n;
    $[16] = phoneNumber;
    $[17] = posthog;
    $[18] = props.participants;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== i18n.locale) {
    t8 = I18N_CONF.getTranslatedUrl("contact", {
      locale: i18n.locale
    });
    $[20] = i18n.locale;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== posthog || $[23] !== props.participants) {
    t9 = () => posthog.capture("plp.promotion.click", {
      channel: "form",
      participants: props.participants
    });
    $[22] = posthog;
    $[23] = props.participants;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== i18n) {
    t10 = i18n._("activity.plp.promotion.cta");
    $[25] = i18n;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t8 || $[29] !== t9) {
    t11 = <ButtonLink href={t8} variant="secondary" size="lg" iconBefore="mail" onClick={t9}>{t10}</ButtonLink>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t7) {
    t12 = <div className="flex shrink-0 flex-col gap-6 @xl:flex-row @xl:items-center @xl:gap-10">{t7}{t11}</div>;
    $[31] = t11;
    $[32] = t7;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t0 || $[35] !== t12 || $[36] !== t6) {
    t13 = <aside data-testid="activity-plp-promotion-card" className={t0}>{t1}{t6}{t12}</aside>;
    $[34] = t0;
    $[35] = t12;
    $[36] = t6;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};